import { useSoftphone } from "@dialstack/sdk-react/softphone";

import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

import { useAppDispatch } from "app/hooks";

import { browserLegDeclined } from "../device-call.slice";
import type { CallBarState } from "./bar-facts.util";
import {
  callControlDangerSx,
  callControlSuccessSx,
  callControlSx
} from "./dialstack-softphone-widget.styles";
import { isRinging } from "./use-inbound-call-pops.hook";

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];

// Which two controls a call needs right now: the pair that sits on the right
// of the folded bar and in the unfolded pop's header (ENG-2226).
export function callBarState(call: SoftphoneCall): CallBarState {
  if (call.direction === "inbound" && isRinging(call)) return "ringing";
  if (call.state === "held" || call.isHeld) return "held";
  if (isRinging(call)) return "calling";
  return "active";
}

// The two round buttons for one call, bound to that call rather than the
// foreground one, wired straight to the SDK (`answerCall`, and the per-call
// actions from `callActionsFor`): Decline · Answer while it rings, Mute · End
// once it is up, Resume · End while it is held.
export default function SoftphoneCallControls({
  call,
  state
}: {
  call: SoftphoneCall;
  state: CallBarState;
}) {
  const dispatch = useAppDispatch();
  const { answerCall, actions, isMerged, hangupConference } = useSoftphone();
  const forCall = actions.callActionsFor(call);
  // A merged conference is one conversation: End drops every leg of it.
  const hangup = () => (isMerged ? hangupConference() : forCall.hangup());
  // The platform ends a declined leg with the same reason as one answered
  // elsewhere, so the decline is noted here: no "Connecting…" hold for it
  // (ENG-2605).
  const decline = () => {
    dispatch(browserLegDeclined(call.id));
    forCall.reject();
  };

  if (state === "ringing") {
    return (
      <Stack direction="row" spacing={1} className="suno-phone-no-drag">
        <Tooltip title="Decline">
          <IconButton
            aria-label="Decline"
            onClick={decline}
            sx={[callControlSx, callControlDangerSx]}
          >
            <CallEndIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Answer">
          <IconButton
            aria-label="Answer"
            onClick={() => answerCall(call)}
            sx={[callControlSx, callControlSuccessSx]}
          >
            <CallIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} className="suno-phone-no-drag">
      {state === "held" ? (
        <Tooltip title="Resume">
          <IconButton
            aria-label="Resume"
            onClick={() => forCall.toggleHold()}
            sx={callControlSx}
          >
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={call.isMuted ? "Unmute" : "Mute"}>
          <IconButton
            aria-label={call.isMuted ? "Unmute" : "Mute"}
            aria-pressed={call.isMuted}
            onClick={() => forCall.toggleMute()}
            sx={callControlSx}
          >
            {call.isMuted ? (
              <MicOffIcon fontSize="small" />
            ) : (
              <MicIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="End call">
        <IconButton
          aria-label="End call"
          onClick={hangup}
          sx={[callControlSx, callControlDangerSx]}
        >
          <CallEndIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
